import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/landing/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { fetchProfile, updateProfile, changePassword, API } from "@/lib/api";
import { cms } from "@/lib/cms";
import { toast } from "sonner";
import {
  ArrowLeft, Camera, Loader2, ShieldCheck, KeyRound, MessageCircle,
  User as UserIcon, Mail, Smartphone, Save, CheckCircle2, Lock, Clock, AlertCircle,
  Palette, Sun, Moon,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useTheme } from "@/contexts/ThemeContext";

const absoluteUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/uploads")) return `${API}/storage${url}`;
  return `${API.replace("/api", "")}${url}`;
};

const initials = (name = "", email = "") => {
  const base = (name || email || "AD").trim();
  const parts = base.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
};

const SectionCard = ({ title, subtitle, icon: Icon, children, testId }) => (
  <motion.section
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    data-testid={testId}
    className="rounded-3xl bg-white border border-slate-200 shadow-[0_2px_30px_-12px_rgba(2,6,23,0.08)] p-6 sm:p-7"
  >
    <div className="flex items-start gap-3 mb-5">
      <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-50 to-blue-50 ring-1 ring-emerald-100 flex items-center justify-center">
        <Icon className="h-5 w-5 text-emerald-700" />
      </div>
      <div>
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h2>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {children}
  </motion.section>
);

export default function AdminProfile() {
  const nav = useNavigate();
  const { user, setUser, logout } = useAuth();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Editable copy
  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileRef = useRef(null);

  // Password change
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const p = await fetchProfile();
      setProfile(p);
      setName(p?.name || "");
      setWhatsapp(p?.whatsapp_number || "9289505505");
      setAvatarUrl(p?.avatar_url || "");
    } catch (err) {
      toast.error("Could not load profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dirty = useMemo(() => {
    if (!profile) return false;
    return (
      (name || "") !== (profile.name || "") ||
      (whatsapp || "") !== (profile.whatsapp_number || "") ||
      (avatarUrl || "") !== (profile.avatar_url || "")
    );
  }, [name, whatsapp, avatarUrl, profile]);

  const onPickAvatar = () => fileRef.current?.click();
  const onAvatarChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\//.test(f.type)) {
      toast.error("Please pick an image file");
      return;
    }
    setUploadingAvatar(true);
    try {
      const r = await cms.upload(f);
      setAvatarUrl(r.url); // store relative path; resolve to absolute when rendering
      toast.success("Avatar uploaded — remember to save changes");
    } catch (err) {
      toast.error("Could not upload image");
    } finally {
      setUploadingAvatar(false);
      e.target.value = "";
    }
  };

  const saveProfile = async () => {
    if (!whatsapp || whatsapp.replace(/\D/g, "").length < 8) {
      toast.error("WhatsApp number must be at least 8 digits");
      return;
    }
    setSavingProfile(true);
    try {
      const updated = await updateProfile({
        name,
        whatsapp_number: whatsapp,
        avatar_url: avatarUrl,
      });
      setProfile(updated);
      // Refresh top-bar user context with the new name
      setUser((u) => ({ ...(u || {}), name: updated.name }));
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (newPw !== confirmPw) { toast.error("Passwords do not match"); return; }
    if (newPw === oldPw) { toast.error("New password must differ from current"); return; }
    setSavingPassword(true);
    try {
      await changePassword(oldPw, newPw);
      toast.success("Password updated. Please sign in again.");
      setOldPw(""); setNewPw(""); setConfirmPw("");
      setTimeout(() => { logout(); nav("/admin/login", { replace: true }); }, 900);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="admin-profile-loading">
        <Loader2 className="h-7 w-7 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] relative" data-testid="admin-profile-page">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-gradient-to-b from-blue-50/60 via-white to-transparent" />

      <header className="relative bg-white/70 backdrop-blur-xl border-b border-slate-200/70 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="hidden sm:inline-flex items-center text-[11px] font-bold tracking-wider uppercase px-2 py-1 rounded-full bg-slate-900 text-white">Admin</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => nav("/admin")} className="rounded-full" data-testid="profile-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to dashboard
          </Button>
        </div>
      </header>

      <main className="relative max-w-5xl mx-auto px-6 lg:px-8 py-8 sm:py-10 space-y-6">
        {/* Hero card */}
        <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 text-white p-6 sm:p-8 ring-1 ring-slate-900/40 shadow-[0_20px_50px_-20px_rgba(2,6,23,0.5)]" data-testid="profile-hero">
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl" />
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="relative">
              <div className="h-20 w-20 rounded-2xl ring-2 ring-white/20 overflow-hidden bg-gradient-to-br from-emerald-400 to-blue-500 flex items-center justify-center text-2xl font-extrabold tracking-tight" data-testid="profile-avatar">
                {avatarUrl ? (
                  <img src={absoluteUrl(avatarUrl)} alt={name} className="h-full w-full object-cover" />
                ) : (
                  initials(name, profile?.email)
                )}
              </div>
              <button
                type="button"
                onClick={onPickAvatar}
                disabled={uploadingAvatar}
                data-testid="profile-avatar-edit"
                className="absolute -bottom-1.5 -right-1.5 h-8 w-8 rounded-full bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center ring-2 ring-slate-900 disabled:opacity-60"
                title="Change avatar"
              >
                {uploadingAvatar ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              </button>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onAvatarChange} data-testid="profile-avatar-input" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-blue-300/80">
                <ShieldCheck className="h-3.5 w-3.5" /> Administrator
              </div>
              <h1 className="mt-1 text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                {name || profile?.email || "Admin"}
              </h1>
              <div className="mt-1 text-sm text-slate-300 truncate">{profile?.email}</div>
            </div>
          </div>
        </section>

        {/* Profile details */}
        <SectionCard
          title="Profile details"
          subtitle="Your identity inside the Wappit Admin Console."
          icon={UserIcon}
          testId="section-profile-details"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="p-name">Full name</Label>
              <Input id="p-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="profile-name-input" />
            </div>
            <div>
              <Label htmlFor="p-username">Username</Label>
              <div className="relative mt-1.5">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="p-username" value={(profile?.email || "").split("@")[0]} readOnly className="pl-9 bg-slate-50 text-slate-600" data-testid="profile-username-input" />
              </div>
              <p className="mt-1 text-[11px] text-slate-500">Username is derived from your admin email.</p>
            </div>
            <div>
              <Label htmlFor="p-email">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="p-email" value={profile?.email || ""} readOnly className="pl-9 bg-slate-50 text-slate-600" data-testid="profile-email-input" />
              </div>
              <p className="mt-1 text-[11px] text-slate-500">Contact Wappit Support to change your email.</p>
            </div>
            <div>
              <Label htmlFor="p-wa">WhatsApp number (OTP)</Label>
              <div className="relative mt-1.5">
                <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="p-wa"
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value.replace(/[^0-9+\s-]/g, ""))}
                  className="pl-9"
                  placeholder="9289505505"
                  data-testid="profile-whatsapp-input"
                />
              </div>
              <p className="mt-1 text-[11px] text-slate-500 flex items-center gap-1">
                <MessageCircle className="h-3 w-3 text-emerald-500" />
                Login OTPs &amp; password-reset codes are delivered here.
              </p>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-2">
            {dirty && (
              <span className="text-xs font-semibold text-amber-700 flex items-center gap-1" data-testid="profile-unsaved-indicator">
                <AlertCircle className="h-3.5 w-3.5" /> Unsaved changes
              </span>
            )}
            <Button
              onClick={saveProfile}
              disabled={!dirty || savingProfile}
              data-testid="profile-save-btn"
              className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
            >
              {savingProfile ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
              Save changes
            </Button>
          </div>
        </SectionCard>

        {/* Change password */}
        <SectionCard
          title="Change password"
          subtitle="Use at least 8 characters. After updating, you will need to sign in again."
          icon={KeyRound}
          testId="section-change-password"
        >
          <form onSubmit={submitPassword} className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="change-password-form">
            <div>
              <Label htmlFor="cp-old">Current password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="cp-old" type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} required className="pl-9" data-testid="cp-old-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="cp-new">New password</Label>
              <Input id="cp-new" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={8} className="mt-1.5" data-testid="cp-new-input" />
            </div>
            <div>
              <Label htmlFor="cp-confirm">Confirm new password</Label>
              <Input id="cp-confirm" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required minLength={8} className="mt-1.5" data-testid="cp-confirm-input" />
            </div>
            <div className="sm:col-span-3 flex justify-end">
              <Button
                type="submit"
                disabled={savingPassword || !oldPw || !newPw || !confirmPw}
                data-testid="cp-submit-btn"
                className="rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold"
              >
                {savingPassword ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <KeyRound className="h-4 w-4 mr-1.5" />}
                Update password
              </Button>
            </div>
          </form>
        </SectionCard>

        {/* Appearance / Theme */}
        <SectionCard
          title="Appearance"
          subtitle="A single switch controls the theme for both the public website and the admin dashboard."
          icon={Palette}
          testId="section-appearance"
        >
          <AppearanceToggle />
        </SectionCard>

        {/* Security settings */}
        <SectionCard
          title="Security"
          subtitle="Two-step verification status and account protections."
          icon={ShieldCheck}
          testId="section-security"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-2xl border border-emerald-200/70 bg-emerald-50/60 p-4 flex items-start gap-3" data-testid="security-2fa">
              <div className="h-9 w-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <CheckCircle2 className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-slate-900">WhatsApp two-step verification</div>
                <div className="text-xs text-slate-600 mt-0.5">Required on every sign-in. A 6-digit code is sent to your WhatsApp number.</div>
                <div className="text-[11px] mt-1 font-semibold text-emerald-700">ENABLED</div>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-start gap-3" data-testid="security-session-ttl">
              <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
                <Clock className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-slate-900">Session expiry</div>
                <div className="text-xs text-slate-600 mt-0.5">Your sign-in session automatically expires after 24 hours.</div>
              </div>
            </div>
          </div>
        </SectionCard>

        {/* Session info */}
        <SectionCard
          title="Session information"
          subtitle="Where you're currently signed in."
          icon={Clock}
          testId="section-sessions"
        >
          <ul className="divide-y divide-slate-100" data-testid="sessions-list">
            <li className="py-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  This device
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Current</span>
                </div>
                <div className="text-xs text-slate-500">
                  {typeof navigator !== "undefined" ? navigator.userAgent.split(") ").shift() + ")" : "Web browser"}
                </div>
              </div>
              <div className="text-xs text-slate-500">Active now</div>
            </li>
            <li className="py-3 text-xs text-slate-500" data-testid="sessions-future">
              Multi-device session management is coming soon. You&apos;ll be able to revoke other sessions from here.
            </li>
          </ul>
        </SectionCard>
      </main>
    </div>
  );
}

/* ── Appearance toggle: single iOS-style Switch that controls the whole app ── */
function AppearanceToggle() {
  const { theme, setTheme } = useTheme();
  const [saving, setSaving] = useState(false);
  const isDark = theme === "dark";

  const onChange = async (checked) => {
    const next = checked ? "dark" : "light";
    setSaving(true);
    try {
      await setTheme(next);
      toast.success(`Website theme set to ${next === "dark" ? "Dark" : "Light"} mode`);
    } catch (e) {
      toast.error("Could not update theme");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-4 sm:p-5"
      data-testid="appearance-dark-mode-row"
    >
      <div className="flex items-start gap-3 min-w-0">
        <div className={`h-10 w-10 flex-shrink-0 rounded-xl flex items-center justify-center transition-colors ${isDark ? "bg-slate-900 text-amber-300" : "bg-amber-50 text-amber-600"}`}>
          {isDark ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-bold text-slate-900" data-testid="appearance-dark-mode-label">
            Website Dark Mode
          </div>
          <div className="text-xs text-slate-600 mt-0.5">
            {isDark
              ? "Dark theme is active across the public website and dashboard."
              : "Light theme is active across the public website and dashboard."}
          </div>
          <div className="text-[11px] mt-1 text-slate-500">
            One switch. Applies everywhere. Saved to your CMS.
          </div>
        </div>
      </div>
      <Switch
        checked={isDark}
        onCheckedChange={onChange}
        disabled={saving}
        data-testid="appearance-dark-mode-switch"
        aria-label="Toggle website dark mode"
        className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-slate-300"
      />
    </div>
  );
}
